/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/layout/AppShell";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, FunnelSimple, DownloadSimple, FilePdf, FileCsv,
  CalendarBlank, User, Lightning, Clock, ChartBar, Target,
  ArrowsClockwise, Users, ListBullets, Hourglass, Warning, Fingerprint,
  Globe,
} from "@phosphor-icons/react";

const todayISO = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

function fmtMins(m) {
  if (!m && m !== 0) return "—";
  if (m < 60) return `${Math.round(m)}m`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return r === 0 ? `${h}h` : `${h}h ${r}m`;
}
function fmtTs(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

export default function AuditLogs() {
  const [tab, setTab] = useState("logs");
  const [filters, setFilters] = useState({
    action: "",
    user_id: "",
    from_date: daysAgo(7),
    to_date: todayISO(),
  });
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, returned: 0, rows: [] });
  const [analytics, setAnalytics] = useState(null);
  const [actors, setActors] = useState([]);
  const [security, setSecurity] = useState(null);
  const [exporting, setExporting] = useState("");

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [filters]);

  const loadActors = async () => {
    try {
      const r = await api.get("/audit-logs/actors");
      setActors(r.data || []);
    } catch (e) { /* ignore */ }
  };
  const loadLogs = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/audit-logs?${qs}&limit=500`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/audit-logs/analytics?${qs}`);
      setAnalytics(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const loadSecurity = async () => {
    setLoading(true);
    try {
      // Use the date-range from the filter to derive `days` for the API.
      const from = filters.from_date ? new Date(filters.from_date) : null;
      const days = from
        ? Math.max(1, Math.ceil((Date.now() - from.getTime()) / 86400000))
        : 30;
      const r = await api.get(`/security/alerts?days=${days}&limit=500`);
      setSecurity(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  useEffect(() => { loadActors(); }, []);
  useEffect(() => {
    if (tab === "logs") loadLogs();
    else if (tab === "analytics") loadAnalytics();
    else if (tab === "security") loadSecurity();
  }, [tab, filters]);

  const downloadExport = async (kind) => {
    setExporting(kind);
    try {
      const tok = localStorage.getItem("rescigo_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/audit-logs/export.${kind}?${qs}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${tok}` } });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `audit-logs-${todayISO()}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
      toast.success(`${kind.toUpperCase()} report downloaded`);
    } catch (e) { toast.error(e.message || "Export failed"); }
    setExporting("");
  };

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const reset = () => setFilters({ action: "", user_id: "",
    from_date: daysAgo(7), to_date: todayISO() });
  const presetDays = (n) => setFilters(f => ({ ...f,
    from_date: daysAgo(n), to_date: todayISO() }));

  return (
    <AppShell title="Audit Logs" subtitle="System">
      {/* Filter Bar */}
      <div className="bg-white border border-[#E5E2D9] rounded-md p-4 mb-4"
            data-testid="audit-filters">
        <div className="flex items-center gap-2 mb-3">
          <FunnelSimple size={14} color="#2D5A27" weight="duotone"/>
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#5C5A55]">
            Filters
          </div>
          <div className="flex-1"/>
          <div className="flex gap-1">
            {[
              ["1d", 1], ["7d", 7], ["30d", 30], ["90d", 90],
            ].map(([l, n]) => (
              <button key={l} onClick={() => presetDays(n)}
                      data-testid={`preset-${l}`}
                      className="text-[11px] px-2.5 py-1 rounded border border-[#E5E2D9] hover:border-[#2D5A27] hover:text-[#2D5A27] text-[#5C5A55]">
                {l}
              </button>
            ))}
            <button onClick={reset} data-testid="reset-filters"
                    className="text-[11px] px-2.5 py-1 rounded border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]">
              <ArrowsClockwise size={11} weight="bold" className="inline -mt-0.5 mr-1"/>Reset
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="From" Icon={CalendarBlank}>
            <input type="date" value={filters.from_date}
                    onChange={e => setF("from_date", e.target.value)}
                    data-testid="filter-from"
                    className="h-10 w-full px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </Field>
          <Field label="To" Icon={CalendarBlank}>
            <input type="date" value={filters.to_date}
                    onChange={e => setF("to_date", e.target.value)}
                    data-testid="filter-to"
                    className="h-10 w-full px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </Field>
          <Field label="Staff / User" Icon={User}>
            <select value={filters.user_id}
                    onChange={e => setF("user_id", e.target.value)}
                    data-testid="filter-user"
                    className="h-10 w-full px-3 border border-[#E5E2D9] rounded-md text-sm bg-white">
              <option value="">All users</option>
              {actors.map(a => (
                <option key={a.user_id} value={a.user_id}>
                  {a.name}{a.email ? ` · ${a.email}` : ""} · {a.events} events
                </option>
              ))}
            </select>
          </Field>
          <Field label="Action contains" Icon={Lightning}>
            <input value={filters.action}
                    onChange={e => setF("action", e.target.value)}
                    placeholder="e.g. partner.create"
                    data-testid="filter-action"
                    className="h-10 w-full px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-[#F1EFE7]">
          <button onClick={() => downloadExport("csv")}
                  disabled={!!exporting}
                  data-testid="export-csv"
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-md border border-[#E5E2D9] text-sm text-[#1A1A1A] hover:bg-[#F7F6F2] disabled:opacity-60">
            <FileCsv size={14} weight="duotone" color="#2D5A27"/>
            {exporting === "csv" ? "Preparing…" : "Download CSV"}
          </button>
          <button onClick={() => downloadExport("pdf")}
                  disabled={!!exporting}
                  data-testid="export-pdf"
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm text-white bg-[#2D5A27] hover:bg-[#234a1f] disabled:opacity-60">
            <FilePdf size={14} weight="duotone"/>
            {exporting === "pdf" ? "Preparing…" : "Download PDF Report"}
          </button>
          <div className="flex-1"/>
          <div className="text-[11px] text-[#8A8780]">
            Window: <b>{filters.from_date || "beginning"}</b> → <b>{filters.to_date || "now"}</b>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4">
        {[
          { id: "logs", label: "Event Log", Icon: ListBullets },
          { id: "analytics", label: "Analytics", Icon: ChartBar },
          { id: "security", label: "Security Alerts", Icon: Warning },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
                  data-testid={`tab-${t.id}`}
                  className={`inline-flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium transition
                    ${tab === t.id
                      ? (t.id === "security" ? "bg-[#B12A1E] text-white" : "bg-[#2D5A27] text-white")
                      : "border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2]"}`}>
            <t.Icon size={14} weight="duotone"/> {t.label}
          </button>
        ))}
      </div>

      {tab === "logs" ? (
        <LogsTab data={data} loading={loading}/>
      ) : tab === "analytics" ? (
        <AnalyticsTab data={analytics} loading={loading}/>
      ) : (
        <SecurityTab data={security} loading={loading} reload={loadSecurity}/>
      )}
    </AppShell>
  );
}

function Field({ label, Icon, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] mb-1 flex items-center gap-1">
        {Icon ? <Icon size={11} weight="duotone" color="#2D5A27"/> : null} {label}
      </div>
      {children}
    </label>
  );
}

/* ---------------- LOGS TAB ---------------- */
function LogsTab({ data, loading }) {
  const { total = 0, returned = 0, rows = [] } = data || {};
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md"
          data-testid="audit-logs-tab">
      <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
        <ShieldCheck size={18} color="#2D5A27" weight="fill"/>
        <div className="font-heading">Tamper-evident audit trail</div>
        <div className="flex-1"/>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
          Showing {returned} of {total} events
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-6 py-3">Timestamp</th>
              <th className="px-6 py-3">Action</th>
              <th className="px-6 py-3">Actor</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Target</th>
              <th className="px-6 py-3">Meta</th>
              <th className="px-6 py-3">IP</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-[#8A8780]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-[#8A8780]">No events match these filters.</td></tr>
            )}
            {!loading && rows.map((l, i) => (
              <tr key={l.id} data-testid={`log-row-${l.id}`}
                  className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                <td className="px-6 py-3 font-mono text-xs text-[#5C5A55] whitespace-nowrap">{fmtTs(l.timestamp)}</td>
                <td className="px-6 py-3 font-mono text-xs"><span className="text-[#2D5A27]">{l.action}</span></td>
                <td className="px-6 py-3">{l.actor || "—"}</td>
                <td className="px-6 py-3 text-xs text-[#5C5A55]">{l.actor_email || "—"}</td>
                <td className="px-6 py-3 font-mono text-[11px] text-[#5C5A55] truncate max-w-[180px]">{l.target || "—"}</td>
                <td className="px-6 py-3 font-mono text-[11px] text-[#5C5A55] truncate max-w-[220px]">
                  {l.meta && Object.keys(l.meta).length > 0 ? JSON.stringify(l.meta) : "—"}
                </td>
                <td className="px-6 py-3 font-mono text-[11px] text-[#8A8780]">{l.ip || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------------- ANALYTICS TAB ---------------- */
function AnalyticsTab({ data, loading }) {
  if (loading || !data) {
    return (
      <div className="bg-white border border-[#E5E2D9] rounded-md p-12 text-center text-[#8A8780]">
        {loading ? "Crunching the numbers…" : "No analytics yet."}
      </div>
    );
  }
  const { totals, per_user, top_visited, most_spent, inactive_periods } = data;

  return (
    <div className="space-y-4" data-testid="audit-analytics-tab">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Kpi label="Activities" value={totals.total_activities} Icon={Lightning} accent="#2D5A27"/>
        <Kpi label="Distinct users" value={totals.distinct_users} Icon={Users} accent="#0B2B66"/>
        <Kpi label="Active time" value={fmtMins(totals.active_minutes)} Icon={Clock} accent="#2D5A27"/>
        <Kpi label="Inactive time" value={fmtMins(totals.inactive_minutes)} Icon={Hourglass} accent="#B12A1E"/>
        <Kpi label="Inactive periods" value={totals.inactive_periods_count} Icon={Hourglass} accent="#C9A227"/>
        <Kpi label="Distinct actions" value={totals.distinct_actions} Icon={Target} accent="#0B2B66"/>
      </div>

      {/* Per-user table */}
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
          <Users size={16} color="#0B2B66" weight="duotone"/>
          <div className="font-heading">Per-user activity rollup</div>
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            {per_user.length} users
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="per-user-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Events</th>
                <th className="px-4 py-3 text-right">Active</th>
                <th className="px-4 py-3 text-right">Inactive</th>
                <th className="px-4 py-3">First</th>
                <th className="px-4 py-3">Last</th>
                <th className="px-4 py-3">Top page</th>
              </tr>
            </thead>
            <tbody>
              {per_user.length === 0 && (
                <tr><td colSpan={8} className="px-6 py-8 text-center text-[#8A8780]">No user activity in this window.</td></tr>
              )}
              {per_user.map((u, i) => (
                <tr key={u.user_id} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}
                    data-testid={`per-user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A1A1A]">{u.name}</div>
                    <div className="text-[11px] text-[#8A8780]">{u.email || u.user_id.slice(0, 8)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#0B2B66]/8 text-[#0B2B66]">
                      {u.role || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{u.events_count}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#2D5A27]">{fmtMins(u.active_minutes)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#B12A1E]">{fmtMins(u.inactive_minutes)}</td>
                  <td className="px-4 py-3 text-[11px] text-[#5C5A55] whitespace-nowrap">{fmtTs(u.first)}</td>
                  <td className="px-4 py-3 text-[11px] text-[#5C5A55] whitespace-nowrap">{fmtTs(u.last)}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#2D5A27]">
                    {u.top_actions?.[0]?.[0] || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top pages and time-spent side-by-side */}
      <div className="grid md:grid-cols-2 gap-4">
        <ListCard title="Most visited pages / actions"
                   testid="top-visited"
                   subtitle="By event count"
                   rows={top_visited.map(r => ({ label: r.action, value: r.count }))}
                   metricLabel="events"
                   accent="#0B2B66"/>
        <ListCard title="Most time spent"
                   testid="most-spent"
                   subtitle="By accumulated active minutes"
                   rows={most_spent.map(r => ({ label: r.action, value: r.minutes,
                                                  display: fmtMins(r.minutes) }))}
                   metricLabel="time"
                   accent="#2D5A27"/>
      </div>

      {/* Inactive periods */}
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
          <Hourglass size={16} color="#B12A1E" weight="duotone"/>
          <div className="font-heading">Inactive periods (gaps &gt; 30 min)</div>
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            {inactive_periods.length} recent
          </div>
        </div>
        {inactive_periods.length === 0 ? (
          <div className="px-6 py-8 text-center text-[#8A8780] text-sm">No inactive periods detected — continuous activity.</div>
        ) : (
          <table className="w-full text-sm" data-testid="inactive-periods-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-4 py-3">User</th>
                <th className="px-4 py-3">From</th>
                <th className="px-4 py-3">To</th>
                <th className="px-4 py-3 text-right">Duration</th>
                <th className="px-4 py-3">After action</th>
              </tr>
            </thead>
            <tbody>
              {inactive_periods.map((p, i) => (
                <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-4 py-3">{p.user}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#5C5A55]">{fmtTs(p.from)}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#5C5A55]">{fmtTs(p.to)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#B12A1E]">{fmtMins(p.minutes)}</td>
                  <td className="px-4 py-3 font-mono text-[11px] text-[#2D5A27]">{p.after_action || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value, Icon, accent }) {
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md p-4 relative overflow-hidden">
      <div className="absolute -right-3 -top-3 w-14 h-14 rounded-full opacity-[0.08]"
            style={{ background: accent }}/>
      <Icon size={14} weight="duotone" color={accent}/>
      <div className="font-heading text-2xl mt-2" style={{ color: accent }}>{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780] mt-0.5">{label}</div>
    </div>
  );
}

function ListCard({ title, subtitle, rows, metricLabel, accent, testid }) {
  const max = rows.reduce((m, r) => Math.max(m, r.value), 0) || 1;
  return (
    <div className="bg-white border border-[#E5E2D9] rounded-md" data-testid={testid}>
      <div className="px-6 py-4 border-b border-[#E5E2D9]">
        <div className="font-heading">{title}</div>
        <div className="text-[11px] text-[#8A8780] mt-0.5">{subtitle}</div>
      </div>
      <div className="p-4 space-y-2">
        {rows.length === 0 && (
          <div className="text-center text-[#8A8780] text-sm py-6">No data.</div>
        )}
        {rows.slice(0, 12).map((r, i) => (
          <div key={i} className="flex items-center gap-3">
            <div className="w-5 text-[11px] font-mono text-[#8A8780] text-right">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-xs text-[#1A1A1A] truncate">{r.label}</div>
              <div className="h-1.5 mt-1 rounded-full bg-[#F1EFE7] overflow-hidden">
                <div className="h-full rounded-full"
                      style={{ width: `${(r.value / max) * 100}%`, background: accent }}/>
              </div>
            </div>
            <div className="text-xs font-mono text-[#5C5A55] w-20 text-right">
              {r.display || `${r.value} ${metricLabel}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ---------------- SECURITY ALERTS TAB (Iter 237) ---------------- */
function SecurityTab({ data, loading, reload }) {
  const [filter, setFilter] = useState("all");   // all | probe | adentry_visit | new_devices
  if (loading || !data) {
    return (
      <div className="bg-white border border-[#E5E2D9] rounded-md p-12 text-center text-[#8A8780]"
             data-testid="security-tab-loading">
        {loading ? "Scanning intrusion attempts…" : "No security data yet."}
      </div>
    );
  }
  const { totals = {}, top_ips = [], top_paths = [], rows = [] } = data;
  const filtered = rows.filter(r => {
    if (filter === "all")           return true;
    if (filter === "probe")         return r.kind === "probe";
    if (filter === "adentry_visit") return r.kind === "adentry_visit";
    if (filter === "new_devices")   return r.kind === "adentry_visit" && (r.visits || 1) === 1;
    return true;
  });
  return (
    <div className="space-y-4" data-testid="security-tab">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Bot probes" value={totals.probes} Icon={Warning} accent="#B12A1E"/>
        <Kpi label="/adentry visits" value={totals.adentry_visits} Icon={ShieldCheck} accent="#0B2B66"/>
        <Kpi label="New devices" value={totals.new_devices} Icon={Fingerprint} accent="#C9A227"/>
        <Kpi label="Top offender hits"
              value={top_ips[0]?.hits || 0} Icon={Globe} accent="#2D5A27"/>
      </div>
      {/* Top IPs + Top paths */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
            <Globe size={14} weight="duotone" color="#B12A1E"/>
            <div className="font-heading text-sm">Top offending IPs</div>
            <div className="flex-1"/>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
              {top_ips.length} unique
            </div>
          </div>
          {top_ips.length === 0 ? (
            <div className="p-6 text-sm text-[#8A8780] text-center">No probe activity in this window.</div>
          ) : (
            <div className="divide-y" data-testid="security-top-ips">
              {top_ips.map((r, i) => (
                <div key={r.ip} className="p-3 flex items-start gap-3">
                  <div className="w-6 text-[11px] font-mono text-[#8A8780] text-right">{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-[#1A1A1A]">{r.ip}</div>
                    <div className="text-[10px] text-[#8A8780] mt-0.5 truncate">
                      {(r.paths_sample || []).slice(0, 3).join(" · ") || "—"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-mono text-[#B12A1E]">{r.hits}</div>
                    <div className="text-[10px] text-[#8A8780]">hits</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-[#E5E2D9] rounded-md">
          <div className="px-4 py-3 border-b border-[#E5E2D9] flex items-center gap-2">
            <Target size={14} weight="duotone" color="#B12A1E"/>
            <div className="font-heading text-sm">Most-probed paths</div>
            <div className="flex-1"/>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">
              {top_paths.length} paths
            </div>
          </div>
          {top_paths.length === 0 ? (
            <div className="p-6 text-sm text-[#8A8780] text-center">No probes logged in this window.</div>
          ) : (
            <div className="divide-y" data-testid="security-top-paths">
              {top_paths.map((r, i) => (
                <div key={r.path} className="p-3 flex items-center gap-3">
                  <div className="w-6 text-[11px] font-mono text-[#8A8780] text-right">{i + 1}</div>
                  <div className="flex-1 font-mono text-xs truncate">{r.path}</div>
                  <div className="text-sm font-mono text-[#B12A1E]">{r.hits}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filter chips + refresh */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          ["all",           "All events"],
          ["probe",         "Bot probes"],
          ["adentry_visit", "/adentry visits"],
          ["new_devices",   "New devices only"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setFilter(id)}
                    data-testid={`security-filter-${id}`}
                    className={`text-[11px] px-3 py-1.5 rounded border ${
                      filter === id
                        ? "bg-[#B12A1E] text-white border-[#B12A1E]"
                        : "bg-white text-[#5C5A55] border-[#E5E2D9] hover:bg-[#F7F6F2]"}`}>
            {label}
          </button>
        ))}
        <div className="flex-1"/>
        <button onClick={reload} data-testid="security-refresh"
                  className="text-[11px] px-3 py-1.5 rounded border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2] inline-flex items-center gap-1">
          <ArrowsClockwise size={11} weight="bold"/> Refresh
        </button>
      </div>

      {/* Event feed */}
      <div className="bg-white border border-[#E5E2D9] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E2D9] flex items-center gap-2">
          <Warning size={16} weight="fill" color="#B12A1E"/>
          <div className="font-heading">Security event feed</div>
          <div className="flex-1"/>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#8A8780]">
            {filtered.length} events
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="security-feed-table">
            <thead>
              <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Kind</th>
                <th className="px-4 py-3">Path / Device</th>
                <th className="px-4 py-3">IP</th>
                <th className="px-4 py-3">User-Agent</th>
                <th className="px-4 py-3">Detail</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-[#8A8780]">
                  No events match this filter.
                </td></tr>
              )}
              {filtered.map((r, i) => (
                <tr key={r.id} data-testid={`security-row-${r.id}`}
                      className={i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"}>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-[#5C5A55] whitespace-nowrap">
                    {fmtTs(r.at)}
                  </td>
                  <td className="px-4 py-2.5">
                    {r.kind === "probe" ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEE2E2] text-[#B12A1E]">
                        <Warning size={10} weight="fill"/> Probe
                      </span>
                    ) : (r.visits || 1) === 1 ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#FEF3C7] text-[#B45309]">
                        <Fingerprint size={10} weight="fill"/> New device
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-[#E0E7FF] text-[#4338CA]">
                        Revisit ×{r.visits}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-[#1A1A1A] max-w-[240px] truncate">
                    {r.kind === "probe"
                      ? (r.path || "—")
                      : `${r.platform || "?"} · ${r.screen || ""} · ${r.tz || ""} · ${r.lang || ""}`}
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-[#5C5A55]">{r.ip || "—"}</td>
                  <td className="px-4 py-2.5 text-[10px] text-[#8A8780] max-w-[240px] truncate">
                    {r.user_agent || r.last_ua || "—"}
                  </td>
                  <td className="px-4 py-2.5 text-[11px] text-[#5C5A55]">
                    {r.kind === "probe"
                      ? <>Pattern <code className="text-[#B12A1E]">{r.pattern}</code>
                          {r.referer ? <> · ref <span className="text-[#8A8780]">{r.referer.slice(0, 40)}</span></> : null}</>
                      : <>Fingerprint <code>{(r.fingerprint || "").slice(0, 12)}</code>
                          {r.referrer ? <> · ref <span className="text-[#8A8780]">{r.referrer.slice(0, 40)}</span></> : null}</>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
